import { useState, useEffect, useMemo } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Users, DollarSign, TrendingUp, FileText, Loader2, Calendar } from 'lucide-react';

export default function DataExportPage() {
  const [downloading, setDownloading] = useState('');

  // ─── Client Statement widget state ───
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [period, setPeriod] = useState('current_month');
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stmtLoading, setStmtLoading] = useState(false);

  useEffect(() => {
    api.get('/clients').then(r => {
      const list = r.data || [];
      setClients(list);
      if (list.length > 0) setClientId(list[0].id);
    }).catch(() => {});
  }, []);

  // Last 12 months for dropdown
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      opts.push({ value, label });
    }
    return opts;
  }, []);

  const handleExport = async (type, label) => {
    setDownloading(type);
    try {
      const res = await api.get(`/exports/csv/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${type}-export.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} exported`);
    } catch { toast.error('Export failed'); }
    finally { setDownloading(''); }
  };

  const handleDownloadStatement = async () => {
    if (!clientId) { toast.error('Select a client'); return; }
    // Validation for range
    if (period === 'range') {
      if (!dateFrom || !dateTo) { toast.error('Enter both From and To dates'); return; }
      if (dateFrom > dateTo) { toast.error('From date must be earlier than To date'); return; }
    }
    setStmtLoading(true);
    try {
      const params = {};
      if (period === 'current_month') params.period = 'current_month';
      else if (period === 'month') { params.period = 'month'; params.month = month; }
      else if (period === 'range') { params.period = 'range'; params.date_from = dateFrom; params.date_to = dateTo; }
      else params.period = 'all';

      const res = await api.get(`/exports/client-statement/${clientId}`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const client = clients.find(c => c.id === clientId);
      const tag = period === 'current_month' ? new Date().toISOString().slice(0, 7)
        : period === 'month' ? month
        : period === 'range' ? `${dateFrom}_to_${dateTo}`
        : 'all-time';
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement-${client?.client_id || clientId}-${tag}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Statement downloaded for ${client?.name || 'client'}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to generate statement');
    } finally {
      setStmtLoading(false);
    }
  };

  const exports = [
    { type: 'clients', label: 'Clients Data', desc: 'All clients with ID, name, email, phone, branch, KYC, balance, status', icon: Users, color: 'text-brand-primary' },
    { type: 'transactions', label: 'Transactions', desc: 'All deposits & withdrawals with payment method, currency, status, references', icon: DollarSign, color: 'text-brand-success' },
    { type: 'pnl', label: 'Profit & Loss', desc: 'All P&L entries with gross profit, loss, net P&L, trader, notes', icon: TrendingUp, color: 'text-brand-accent' },
  ];

  const selectedClient = clients.find(c => c.id === clientId);

  return (
    <div className="space-y-6" data-testid="data-export-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">Data Export</h1>
        <p className="text-sm text-brand-text-secondary mt-1">Download system data as CSV files and generate client account statements</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {exports.map(e => (
          <Card key={e.type} className="border border-brand-border bg-white hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><e.icon size={18} className={e.color} /> {e.label}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-xs text-brand-text-secondary mb-4 leading-relaxed">{e.desc}</p>
              <Button onClick={() => handleExport(e.type, e.label)} disabled={downloading === e.type} className="w-full bg-brand-primary text-white rounded-lg text-sm" data-testid={`export-${e.type}-btn`}>
                {downloading === e.type ? <Loader2 size={14} className="animate-spin mr-1" /> : <Download size={14} className="mr-1" />} Export CSV
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Client Statement Generator */}
      <Card className="border border-brand-border bg-white" data-testid="statement-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText size={18} className="text-brand-secondary" /> Client Account Statement (PDF)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-2 space-y-4">
          <p className="text-xs text-brand-text-secondary">
            Generate a professional PDF statement for any client — filtered by <b>Current Month</b>, a <b>specific Month</b>, a <b>Date Range</b>, or <b>All Time</b>.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Client picker */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">Client *</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="bg-white border-brand-border h-10 text-sm" data-testid="stmt-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.client_id} — {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedClient && (
                <p className="text-[11px] text-brand-text-secondary">
                  Balance: <span className="font-mono font-semibold text-brand-primary">${(selectedClient.current_balance || 0).toLocaleString()}</span>
                  {selectedClient.email && <> · {selectedClient.email}</>}
                </p>
              )}
            </div>

            {/* Period picker */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">Period *</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="bg-white border-brand-border h-10 text-sm" data-testid="stmt-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current_month">Current Month</SelectItem>
                  <SelectItem value="month">Specific Month</SelectItem>
                  <SelectItem value="range">Custom Date Range</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Month picker (only when Specific Month) */}
          {period === 'month' && (
            <div className="space-y-1.5" data-testid="stmt-month-picker">
              <Label className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="bg-white border-brand-border h-10 text-sm w-full sm:w-64" data-testid="stmt-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Custom range (only when Date Range) */}
          {period === 'range' && (
            <div className="grid grid-cols-2 gap-4" data-testid="stmt-range-picker">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1">
                  <Calendar size={11} /> From Date
                </Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="bg-white border-brand-border h-10 text-sm" data-testid="stmt-date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1">
                  <Calendar size={11} /> To Date
                </Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="bg-white border-brand-border h-10 text-sm" data-testid="stmt-date-to" />
              </div>
            </div>
          )}

          <div className="pt-2 flex flex-wrap gap-2 items-center">
            <Button
              onClick={handleDownloadStatement}
              disabled={stmtLoading || !clientId}
              className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg h-10 px-6"
              data-testid="download-statement-btn"
            >
              {stmtLoading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}
              Download PDF Statement
            </Button>
            <p className="text-[11px] text-brand-text-secondary">
              Includes deposits, withdrawals, P&amp;L entries, balance summary, and profit share.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
